/**
 * Alert List Component
 * Display and manage wallet tracker alerts
 * Modern redesign with glassmorphism effects
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlertStore, type Alert } from '../../store/alertStore';
import { AlertConfigModal } from './AlertConfigModal';
import { Icons } from '../Icons';
import { formatDistanceToNow } from 'date-fns';
import { exportAlertsToCSV } from '../../utils/csvExport';
import { TRACKER_COLORS } from '../../config/monitorColors';

// ============================================
// CONSTANTS & STYLES
// ============================================

const CONDITION_CONFIG: Record<string, { label: string; color: string; icon: 'ArrowRightLeft' | 'ArrowRight' | 'DollarSign' | 'Activity' }> = {
  any_transaction: { label: 'Any Transaction', color: TRACKER_COLORS.primary, icon: 'Activity' },
  swap: { label: 'Token Swap', color: TRACKER_COLORS.purple, icon: 'ArrowRightLeft' },
  transfer_in: { label: 'Incoming Transfer', color: TRACKER_COLORS.success, icon: 'ArrowRight' },
  transfer_out: { label: 'Outgoing Transfer', color: TRACKER_COLORS.error, icon: 'ArrowRight' },
  value_threshold: { label: 'Value Threshold', color: TRACKER_COLORS.warning, icon: 'DollarSign' },
};

const s = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  },
  alertCard: (enabled: boolean) => ({
    padding: '18px',
    background: enabled ? 'rgba(6, 182, 212, 0.05)' : 'rgba(0, 0, 0, 0.2)',
    borderRadius: '12px',
    border: enabled ? '1px solid rgba(6, 182, 212, 0.15)' : '1px dashed rgba(255, 255, 255, 0.1)',
    opacity: enabled ? 1 : 0.6,
    transition: 'all 0.2s ease',
    marginBottom: '12px',
  }),
  channelBadge: {
    fontSize: '10px',
    padding: '4px 8px',
    borderRadius: '6px',
    background: 'rgba(255, 255, 255, 0.08)',
    color: 'var(--muted)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  actionButton: {
    padding: '8px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--text)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  emptyState: {
    padding: '60px 40px',
    textAlign: 'center' as const,
  },
};

export function AlertList() {
  const { t } = useTranslation();
  const { alerts, removeAlert, toggleAlert } = useAlertStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null);

  const handleEditAlert = (alert: Alert) => {
    setEditingAlert(alert);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAlert(null);
  };

  const getConditionIcon = (condition: string) => {
    const config = CONDITION_CONFIG[condition] || CONDITION_CONFIG.any_transaction;
    const iconName = config.icon;
    const IconComponent = Icons[iconName];
    return <IconComponent size={16} />;
  };

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px', color: 'var(--text)' }}>
            {t('walletTracker.alerts.title', 'Alerts')}
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
            {t('walletTracker.alerts.description', 'Get notified when your tracked wallets make transactions')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {alerts.length > 0 && (
            <button
              onClick={() => exportAlertsToCSV(alerts)}
              style={{
                padding: '10px 16px',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: 'var(--text)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
              }}
              title={t('common.export', 'Export')}
            >
              <Icons.Download size={16} />
              {t('common.export', 'Export')}
            </button>
          )}
          <button
            onClick={() => {
              setEditingAlert(null);
              setIsModalOpen(true);
            }}
            style={{
              padding: '10px 16px',
              background: `linear-gradient(135deg, ${TRACKER_COLORS.primary}, ${TRACKER_COLORS.secondary})`,
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            <Icons.Plus size={16} />
            {t('walletTracker.alerts.newAlert', 'New Alert')}
          </button>
        </div>
      </div>

      {/* Alert List */}
      <div style={{ padding: '20px' }}>
        {alerts.length === 0 ? (
          /* Empty State */
          <div style={s.emptyState}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'rgba(6, 182, 212, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <Icons.Bell size={28} style={{ color: TRACKER_COLORS.primary, opacity: 0.5 }} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
              {t('walletTracker.alerts.empty.title', 'No alerts yet')}
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
              {t('walletTracker.alerts.empty.description', 'Create your first alert to get notified when your tracked wallets make transactions')}
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              style={{
                padding: '12px 24px',
                background: `linear-gradient(135deg, ${TRACKER_COLORS.primary}, ${TRACKER_COLORS.secondary})`,
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              <Icons.Plus size={18} />
              {t('walletTracker.alerts.createAlert', 'Create Alert')}
            </button>
          </div>
        ) : (
          <div>
            {alerts.map((alert) => {
              const conditionConfig = CONDITION_CONFIG[alert.condition] || CONDITION_CONFIG.any_transaction;

              return (
                <div
                  key={alert.id}
                  style={s.alertCard(alert.enabled)}
                  onMouseEnter={(e) => {
                    if (alert.enabled) {
                      e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.3)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(6, 182, 212, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = alert.enabled ? 'rgba(6, 182, 212, 0.15)' : 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    {/* Icon */}
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: alert.enabled ? `${conditionConfig.color}20` : 'rgba(255, 255, 255, 0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: alert.enabled ? conditionConfig.color : 'var(--muted)',
                        flexShrink: 0,
                      }}
                    >
                      {getConditionIcon(alert.condition)}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Wallet Label */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <h4
                          style={{
                            fontSize: '15px',
                            fontWeight: 600,
                            margin: 0,
                            color: 'var(--text)',
                          }}
                        >
                          {alert.walletLabel || `${alert.walletAddress.slice(0, 10)}...`}
                        </h4>
                        {!alert.enabled && (
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: 600,
                              color: TRACKER_COLORS.warning,
                              padding: '2px 8px',
                              background: `${TRACKER_COLORS.warning}15`,
                              borderRadius: '4px',
                            }}
                          >
                            PAUSED
                          </span>
                        )}
                      </div>

                      {/* Condition Description */}
                      <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '0 0 10px 0' }}>
                        <span style={{ color: conditionConfig.color, fontWeight: 500 }}>
                          {conditionConfig.label}
                        </span>
                        {alert.valueThreshold && (
                          <span style={{ marginLeft: '8px' }}>
                            • Min: ${alert.valueThreshold.toLocaleString()}
                          </span>
                        )}
                      </p>

                      {/* Channels */}
                      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                        {alert.channels.map((channel) => (
                          <span key={channel} style={s.channelBadge}>
                            {channel === 'in_app' && '🔔 In-App'}
                            {channel === 'email' && '📧 Email'}
                            {channel === 'webhook' && '⚡ Webhook'}
                          </span>
                        ))}
                      </div>

                      {/* Stats */}
                      <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--muted)' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Icons.Zap size={12} />
                          {alert.triggerCount} {t('walletTracker.alerts.triggers', 'triggers')}
                        </span>
                        {alert.lastTriggered && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Icons.Clock size={12} />
                            {formatDistanceToNow(new Date(alert.lastTriggered), { addSuffix: true })}
                          </span>
                        )}
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Icons.Calendar size={12} />
                          {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button
                        onClick={() => toggleAlert(alert.id)}
                        style={{
                          ...s.actionButton,
                          background: alert.enabled ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                          color: alert.enabled ? TRACKER_COLORS.warning : TRACKER_COLORS.success,
                        }}
                        title={alert.enabled ? t('walletTracker.alerts.pause', 'Pause') : t('walletTracker.alerts.enable', 'Enable')}
                      >
                        {alert.enabled ? <Icons.PauseCircle size={18} /> : <Icons.PlayCircle size={18} />}
                      </button>
                      <button
                        onClick={() => handleEditAlert(alert)}
                        style={s.actionButton}
                        title={t('common.edit', 'Edit')}
                      >
                        <Icons.Edit size={18} />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(t('walletTracker.alerts.confirmDelete', 'Delete this alert?'))) {
                            removeAlert(alert.id);
                          }
                        }}
                        style={{
                          ...s.actionButton,
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: TRACKER_COLORS.error,
                        }}
                        title={t('common.delete', 'Delete')}
                      >
                        <Icons.Trash size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Alert Config Modal */}
      <AlertConfigModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        editingAlert={editingAlert}
      />
    </div>
  );
}
